// @givmo/org-console — the last-module hint (shell contract v3, T1.5).
//
// A CLIENT-SIDE value, per (person, organization), written whenever the console knows which module
// a person is in and read by TWO readers: the doorway, ON A COLD ENTRY ONLY, and the shell's sidebar
// rule, as the frame an Organization-section page wears (`sidebarFor`'s `frameModule` — the
// 2026-09-04 walkthrough ruling). "Is it cheap to bookmark whatever module they were on last?" — it
// is this cheap: one localStorage key that never reaches the server, never read by an in-app LANDING
// control (the wordmark computes with `entry: 'in-app'`, which skips it).
//
// ⚠ IT IS A HINT, NEVER A SOURCE OF TRUTH. The landing rule consults it only when the module it
// names is OPENABLE right now, and the sidebar frame only when that module is mounted here and
// openable, so a stale hint (a module since unmounted or switched off, a grant since removed)
// self-heals to the next branch — the landing rule's next candidate; for the frame the class
// default (`frameFallback`), then the Organization section alone — rather than landing anyone
// somewhere they cannot be.
//
// Keyed per UID so two accounts on one shared browser never inherit each other's last module, and
// per organization so a multi-organization admin's hint for one does not forward them inside the
// other. Cleared for a UID on sign-out. Every access no-ops (never throws) where storage is blocked
// or absent — a sandboxed iframe, a private-mode browser, a server render.

const KEY_PREFIX = 'givmoConsole.lastModule';

/** The exact key for one (person, organization). A uid-less caller uses the `anon` bucket. */
export function lastModuleKey(uid: string | null, orgRef: string): string {
  return `${KEY_PREFIX}.${uid ?? 'anon'}.${orgRef}`;
}

/** The localStorage handle, or `null` where storage is blocked or unavailable. Never throws. */
function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The last module this person was in at this organization on this device, or `null`. */
export function readLastModule(uid: string | null, orgRef: string): string | null {
  const store = storage();
  if (store === null) return null;
  try {
    const value = store.getItem(lastModuleKey(uid, orgRef));
    return value !== null && value.trim().length > 0 ? value.trim() : null;
  } catch {
    return null;
  }
}

/** Remember the module this person is in. No-op (never throws) when storage is unavailable. */
export function writeLastModule(uid: string | null, orgRef: string, slug: string): void {
  const store = storage();
  if (store === null || slug.trim().length === 0) return;
  try {
    store.setItem(lastModuleKey(uid, orgRef), slug.trim());
  } catch {
    // Privacy mode / quota — best effort; the hint simply will not survive.
  }
}

/**
 * Forget every last-module hint for one person, across every organization — the sign-out act.
 *
 * Walks the keys rather than tracking them, because the set of organizations a person has visited
 * on this device is not knowable at sign-out without a second store to keep in sync.
 */
export function clearLastModules(uid: string | null): void {
  const store = storage();
  if (store === null) return;
  try {
    const prefix = `${KEY_PREFIX}.${uid ?? 'anon'}.`;
    const doomed: string[] = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key !== null && key.startsWith(prefix)) doomed.push(key);
    }
    for (const key of doomed) store.removeItem(key);
  } catch {
    // Best effort, as above.
  }
}
